/**
 * =============================================================================
 * ASL Multi-Tenant Partner-Shop: Taxonomy Engine & Category Classifier
 * White-Label Compliant Pure ESM Engine
 * =============================================================================
 */

export const SHOP_CATEGORIES = [
  {
    id: 'cat_alarmtechnik',
    slug: 'alarmtechnik',
    label: 'Gefahrenmeldetechnik',
    shortLabel: 'Gefahrenmeldetechnik',
    icon: 'shield-alert',
    description: 'Alarmzentralen, Melder, Kontakte, Funk und Freigeländesicherung für belastbare Schutzketten.',
    heroTitle: 'Gefahrenmeldetechnik für echte Schutzketten statt Einzelteile.',
    heroText: 'Melder, Kontakte, Funk und Systemkomponenten für Einbruch- und Gefahrenmeldetechnik.',
    advisory: {
      subject: 'Gefahrenmeldetechnik',
      lead: 'Starten Sie nicht beim einzelnen Melder: entscheidend sind Schutzbereich, Systemfamilie und Alarmweg.',
      checkpoints: [
        'Bestandssystem und Funkfamilie vor Zubehör- oder Melderkauf prüfen.',
        'Außenhaut, Bewegung und Freigelände getrennt planen.',
        'Leitstelle, Bedienung und Sabotageschutz früh mitdenken.',
      ],
      ask: 'Wenn Objektgröße, Meldermix oder Funkreichweite unklar sind, wenden Sie sich gerne an unsere Fachberater.',
      shortcuts: ['scharfschaltung', 'zentralen-module', 'bewegungsmelder', 'kontakte-sensoren', 'funksysteme'],
    },
    roots: ['Gefahrenmeldetechnik', 'Alarmtechnik', 'Einbruchmelder'],
    priority: 520,
    subcategories: [
      {
        slug: 'scharfschaltung',
        label: 'Scharfschaltung',
        icon: 'keypad',
        description: 'Bedienteile, Transponderleser und Scharfschalt-Komponenten.',
        matchPrefixes: ['Scharfschaltung', 'INTEGRA > Bedienteile', 'PERFECTA > Bedienung', 'VERSA > Bedienteile', 'Bedienung', 'Bedienteile'],
      },
      {
        slug: 'zentralen-module',
        label: 'Zentralen & Module',
        icon: 'cpu',
        description: 'Alarmzentralen, Erweiterungsmodule und Kommunikationsbausteine.',
        matchPrefixes: ['INTEGRA', 'PERFECTA', 'VERSA', 'Erweiterungsmodule', 'Kommunikation', 'Leitstellentechnik'],
      },
      {
        slug: 'bewegungsmelder',
        label: 'Bewegungsmelder',
        icon: 'radar',
        description: 'PIR-, Dual- und Spezial-Bewegungsmelder für Innen- und Außenbereiche.',
        matchPrefixes: ['Bewegungsmelder', 'PIR', 'Dualmelder', 'BXS'],
      },
      {
        slug: 'kontakte-sensoren',
        label: 'Kontakte & Sensoren',
        icon: 'radio',
        description: 'Magnetkontakte, Erschütterungs-, Glasbruch- und Umgebungssensoren.',
        matchPrefixes: ['Kontakte / Sensoren', 'Magnetkontakte', 'Glasbruchmelder', 'Sensoren'],
      },
      {
        slug: 'funksysteme',
        label: 'Funksysteme',
        icon: 'wifi',
        description: 'Drahtlose Sicherheitssysteme wie ABAX2 und BE WAVE.',
        matchPrefixes: ['ABAX2-Funk', 'BE WAVE'],
      },
      {
        slug: 'signalgeber',
        label: 'Signalgeber',
        icon: 'bell-ring',
        description: 'Akustische und optische Signalgeber für Innen- und Außenmontage.',
        matchPrefixes: ['Signalgeber', 'Sirenen'],
      },
      {
        slug: 'freigelaendesicherung',
        label: 'Freigeländesicherung',
        icon: 'fence',
        description: 'Perimeter- und Freigeländeschutz für anspruchsvolle Außenüberwachung.',
        matchPrefixes: ['Freigeländesicherung'],
      },
      {
        slug: 'sets-aktionen',
        label: 'Sets & Aktionen',
        icon: 'package',
        description: 'Vorkonfigurierte Starterpakete, Systemsets und Aktionsartikel.',
        matchPrefixes: ['Neuigkeiten'],
      },
      {
        slug: 'zubehoer',
        label: 'Zubehör',
        icon: 'wrench',
        description: 'Akkumulatoren, Leergehäuse, Verteiler und Installationszubehör.',
        matchPrefixes: ['Zubehör'],
      },
    ],
  },
  {
    id: 'cat_videoueberwachung',
    slug: 'videoueberwachung',
    label: 'Videoüberwachung',
    shortLabel: 'Videoüberwachung',
    icon: 'camera',
    description: 'Kamerasysteme, Recorder, Monitore und Infrastruktur für professionelle Überwachung.',
    heroTitle: 'Videoüberwachung für saubere Sicht auf jedes Objekt.',
    heroText: 'Von Kameras bis Signalübertragung: hier liegen die Kernbausteine für moderne Videosysteme.',
    advisory: {
      subject: 'Videoüberwachung',
      lead: 'Die richtige Auswahl hängt von Sichtfeld, Licht, Aufzeichnung und Netzwerk ab, nicht nur vom Kameramodell.',
      checkpoints: [
        'Innen, außen, Zufahrt oder Kasse zuerst nach Sichtziel trennen.',
        'Recorder, PoE, Netzwerk und Speicher zusammen betrachten.',
        'Halter, Gehäuse und Signalweg vor der Montage klären.',
      ],
      ask: 'Bei Bestandssystemen, Kennzeichenerkennung, Dunkelzonen oder Netzwerkanbindung unterstützen unsere Video-Experten gerne.',
      shortcuts: ['ip-kameras', 'rekordersysteme', 'signaluebertragung', 'gehaeuse-halter'],
    },
    roots: ['Videoüberwachung', 'Video', 'CCTV'],
    priority: 500,
    subcategories: [
      {
        slug: 'ip-kameras',
        label: 'Kameras',
        icon: 'camera',
        description: 'Professionelle IP-, Koaxial- und Thermalkameras für jede Überwachungsaufgabe.',
        matchPrefixes: ['IP-Kameras', 'Kennzeichenkameras', 'Koax-Kameras', 'Thermalkameras'],
      },
      {
        slug: 'rekordersysteme',
        label: 'Rekordersysteme',
        icon: 'hard-drive',
        description: 'NVR-, DVR- und Recording-Komponenten.',
        matchPrefixes: ['Rekordersysteme'],
      },
      {
        slug: 'signaluebertragung',
        label: 'Signalübertragung',
        icon: 'network',
        description: 'Übertragungstechnik für stabile Bild- und Datensignale.',
        matchPrefixes: ['Signalübertragung'],
      },
      {
        slug: 'serversysteme',
        label: 'Serversysteme',
        icon: 'server',
        description: 'Server- und VMS-nahe Infrastruktur.',
        matchPrefixes: ['Serversysteme'],
      },
      {
        slug: 'stromversorgung',
        label: 'Stromversorgung',
        icon: 'zap',
        description: 'Versorgung, Schutz und PoE-nahe Peripherie.',
        matchPrefixes: ['Stromversorgung'],
      },
      {
        slug: 'gehaeuse-halter',
        label: 'Gehäuse & Halter',
        icon: 'box',
        description: 'Montagezubehör, Halter und Anschlussboxen.',
        matchPrefixes: ['Gehäuse & Halter'],
      },
      {
        slug: 'tuerstationen',
        label: 'Türstationen',
        icon: 'door-open',
        description: 'Stationen für Eingänge, Zufahrten und Türkommunikation.',
        matchPrefixes: ['Türstationen'],
      },
      {
        slug: 'monitore-spezialsysteme',
        label: 'Monitore & Spezialsysteme',
        icon: 'monitor',
        description: 'Monitore, Zubehör, Mobile Lösungen und Spezialthemen.',
        matchPrefixes: ['Monitore', 'Mobile Lösungen', 'Zubehör VT'],
      },
      {
        slug: 'neuheiten-aktionen',
        label: 'Neuheiten & Aktionen',
        icon: 'sparkles',
        description: 'Neue, reduzierte oder aktionsbezogene Videokomponenten.',
        matchPrefixes: ['Neuigkeiten'],
      },
    ],
  },
  {
    id: 'cat_brandmeldetechnik',
    slug: 'brandmeldetechnik',
    label: 'Brandmeldetechnik',
    shortLabel: 'Brandmeldetechnik',
    icon: 'flame',
    description: 'Komponenten für Brandmeldeanlagen, Versorgung, Prüfung und Bussysteme.',
    heroTitle: 'Brandmeldetechnik für zuverlässige Früherkennung und robuste Versorgung.',
    heroText: 'Brandmelder, Netzteile, Bus- und Verteilkomponenten für professionelle Brandmeldesysteme.',
    advisory: {
      subject: 'Brandmeldetechnik',
      lead: 'Brandmeldung ist Systemplanung: Melder, Sockel, Bus und Versorgung müssen zueinander passen.',
      checkpoints: [
        'Meldertyp, Sockel und Zentrale nicht getrennt entscheiden.',
        'Energieversorgung und Signalgeber als Teil der Schutzkette prüfen.',
        'Bestandsanlage, Herstellerbindung und Prüfmittel vorab benennen.',
      ],
      ask: 'Bei Austausch, Erweiterung oder normnaher Planung stehen unsere Brandschutzexperten zur Seite.',
      shortcuts: ['brandmelder', 'adressbus-acsp', 'netzteile', 'signalgeber'],
    },
    roots: ['Brandmeldetechnik', 'Brandschutz', 'EN54'],
    priority: 300,
    subcategories: [
      {
        slug: 'brandmelder',
        label: 'Brandmelder',
        icon: 'flame',
        description: 'Automatische Melder und Sensorkomponenten.',
        matchPrefixes: ['Brandmelder'],
      },
      {
        slug: 'adressbus-acsp',
        label: 'Adressbus & ACSP',
        icon: 'circuit-board',
        description: 'Adressbus-Komponenten, ACSP und Zentralen-nahe Bausteine.',
        matchPrefixes: ['Adressbussystem ACSP', 'Brandmeldezentrale'],
      },
      {
        slug: 'netzteile',
        label: 'Netzteile',
        icon: 'zap',
        description: 'Versorgung für Brandmeldesysteme.',
        matchPrefixes: ['Netzteile'],
      },
      {
        slug: 'signalgeber',
        label: 'Signalgeber',
        icon: 'bell-ring',
        description: 'Akustische und optische Alarmierung.',
        matchPrefixes: ['Signalgeber'],
      },
      {
        slug: 'sockel-verteiler',
        label: 'Sockel & Verteiler',
        icon: 'git-branch',
        description: 'Sockel, Verteiler und Anschlussperipherie.',
        matchPrefixes: ['Brandmeldersockel', 'Verteiler'],
      },
      {
        slug: 'honeywell-technik',
        label: 'Honeywell-Technik',
        icon: 'shield-check',
        description: 'Honeywell-nahe Melder, Sockel und Prüfmittel im Brandmeldebereich.',
        manufacturerHints: ['Honeywell'],
        matchPrefixes: ['Honeywell-Technik'],
      },
      {
        slug: 'zubehoer-aktionen',
        label: 'Zubehör & Aktionen',
        icon: 'wrench',
        description: 'Ergänzende Systembausteine, Prüfmittel und Sonderaktionen.',
        matchPrefixes: ['Zubehör', 'Neuigkeiten'],
      },
    ],
  },
  {
    id: 'cat_zutrittskontrolle',
    slug: 'zutrittskontrolle',
    label: 'Zutrittskontrolle',
    shortLabel: 'Zutrittskontrolle',
    icon: 'key-round',
    description: 'Leser, Controller, Identmedien und Schließhardware für kontrollierte Zugänge.',
    heroTitle: 'Zutrittskontrolle für nachvollziehbare, sichere Zugänge.',
    heroText: 'Vom Terminal bis zum Controller: Komponenten für skalierbare Zutrittslogik.',
    advisory: {
      subject: 'Zutrittskontrolle',
      lead: 'Zutritt beginnt mit Türen, Rollen und Nachvollziehbarkeit, nicht mit einem einzelnen Leser.',
      checkpoints: [
        'Türtyp, Berechtigung und Protokollierung vor Identmedien klären.',
        'Controller, Leser und Transponder als Systemfamilie prüfen.',
        'Zeitmodelle, Ausfallverhalten und Montageort früh festlegen.',
      ],
      ask: 'Wenn mehrere Türen, Rollen oder bestehende Systeme beteiligt sind, helfen unsere Spezialisten bei der Systemarchitektur.',
      shortcuts: ['terminals-leser', 'controller', 'transponder', 'klinkenwaechter'],
    },
    roots: ['Zutrittskontrolle', 'Zutritt', 'Access Control'],
    priority: 200,
    subcategories: [
      {
        slug: 'terminals-leser',
        label: 'Terminals & Leser',
        icon: 'scan-face',
        description: 'Terminals, Leser und Frontends.',
        matchPrefixes: ['Terminals', 'Mifare ZK', 'RFID-Leser'],
      },
      {
        slug: 'controller',
        label: 'Controller',
        icon: 'cpu',
        description: 'Controller und ACCO-NET-nahe Steuerung.',
        matchPrefixes: ['Controller', 'ACCO NET'],
      },
      {
        slug: 'transponder',
        label: 'Transponder',
        icon: 'credit-card',
        description: 'Transponder und Identmedien.',
        matchPrefixes: ['Transponder'],
      },
      {
        slug: 'klinkenwaechter',
        label: 'Klinkenwächter',
        icon: 'door-closed',
        description: 'Mechanisch-elektrische Überwachung von Türen.',
        matchPrefixes: ['Klinkenwächter'],
      },
      {
        slug: 'systemeinrichtung',
        label: 'Systemeinrichtung',
        icon: 'settings',
        description: 'Einrichtung und Inbetriebnahme-nahe Bausteine.',
        matchPrefixes: ['Systemeinrichtung'],
      },
    ],
  },
  {
    id: 'cat_stromversorgung',
    slug: 'stromversorgung-infrastruktur',
    label: 'Stromversorgung & Gehäusetechnik',
    shortLabel: 'Stromversorgung',
    icon: 'zap',
    description: 'Netzteile, Racks, Gehäuse, Netzwerk und Verteillogik rund um stabile Sicherheitsinstallationen.',
    heroTitle: 'Stromversorgung und Gehäusetechnik als Rückgrat des Systems.',
    heroText: 'Netzteile, Racks, Gehäuse und Verteillogik für stabile Sicherheitsinstallationen.',
    advisory: {
      subject: 'Stromversorgung und Gehäusetechnik',
      lead: 'Versorgung und Infrastruktur entscheiden, ob Sicherheitstechnik im Alltag stabil bleibt.',
      checkpoints: [
        'Last, Reserve und Akku zuerst gegen die Gesamtanlage berechnen.',
        'Gehäuse, Rack und Netzwerk nach Montageort und Wartungsfreundlichkeit wählen.',
        'Brandschutz- und EN54-Kontext vor Standardnetzteilen prüfen.',
      ],
      ask: 'Bei Mischsystemen, Notstrom, Brandschutzversorgung oder Rackplanung unterstützen wir Sie gerne vorab.',
      shortcuts: ['netzteile', 'brandschutz-netzteile', '19-zoll-racks', 'netzwerk'],
    },
    roots: ['PULSAR', 'Stromversorgung', 'Infrastruktur', 'Netzteile'],
    priority: 100,
    subcategories: [
      {
        slug: 'netzteile',
        label: 'Netzteile',
        icon: 'zap',
        description: 'Einbau- und Standardnetzteile.',
        matchPrefixes: ['Netzteile'],
      },
      {
        slug: 'brandschutz-netzteile',
        label: 'Brandschutz-Netzteile',
        icon: 'shield-alert',
        description: 'Versorgung für EN54- und Brandschutzumgebungen.',
        matchPrefixes: ['Brandschutz'],
      },
      {
        slug: '19-zoll-racks',
        label: '19"-Racks',
        icon: 'server',
        description: 'Rack-Infrastruktur and Einbausysteme.',
        matchPrefixes: ['19" RACKs'],
      },
      {
        slug: 'netzwerk',
        label: 'Netzwerk',
        icon: 'network',
        description: 'Netzwerknahe Infrastruktur und Peripherie.',
        matchPrefixes: ['Netzwerk'],
      },
      {
        slug: 'metallgehaeuse',
        label: 'Metallgehäuse',
        icon: 'box',
        description: 'Gehäuse für Innen- und Außenbereiche.',
        matchPrefixes: ['Metallgehäuse'],
      },
      {
        slug: 'mehrfach-netzteile',
        label: 'Mehrfach-Netzteile',
        icon: 'split',
        description: 'Mehrkanalige Versorgungen für komplexere Installationen.',
        matchPrefixes: ['Mehrfach-Netzteile'],
      },
      {
        slug: 'relaismodule',
        label: 'Relaismodule',
        icon: 'toggle-left',
        description: 'Relais und Schaltmodule.',
        matchPrefixes: ['Relaismodule'],
      },
      {
        slug: 'zubehoer',
        label: 'Zubehör',
        icon: 'wrench',
        description: 'Akkus, Kabel und ergänzende Infrastruktur.',
        matchPrefixes: ['Zubehör', 'Mean Well', 'Spannungswandler'],
      },
    ],
  },
  {
    id: 'cat_weitere',
    slug: 'weitere-produkte',
    label: 'Weitere Produkte',
    shortLabel: 'Weitere',
    icon: 'layers',
    description: 'Ergänzende Sicherheitsartikel und Zubehör aus dem Sortiment.',
    heroTitle: 'Weitere Produkte',
    heroText: 'Ergänzende Artikel und Zubehör aus dem Sortiment.',
    advisory: {
      subject: 'Weitere Produkte',
      lead: 'Ergänzende Artikel am besten über Einsatzbereich, Hersteller und bestehendes System einordnen.',
      checkpoints: [
        'SKU oder Hersteller suchen, wenn der Artikel bereits bekannt ist.',
        'Bei Zubehör immer die Kompatibilität mit dem Hauptsystem prüfen.',
        'Unklare Zuordnung gerne als Projektanfrage übermitteln.',
      ],
      ask: 'Wenn ein Artikel nicht eindeutig zugeordnet werden kann, senden Sie uns eine kurze Anfrage mit Systemfamilie und Einsatzort.',
      shortcuts: [],
    },
    roots: [],
    priority: 10,
    subcategories: [],
  },
];

export const CATEGORY_MAP = {
  'alarmtechnik': { id: 'cat_alarmtechnik', slug: 'alarmtechnik', name: 'Gefahrenmeldetechnik', sort: 10, icon: 'shield-alert' },
  'videoueberwachung': { id: 'cat_videoueberwachung', slug: 'videoueberwachung', name: 'Videoüberwachung', sort: 20, icon: 'camera' },
  'brandmeldetechnik': { id: 'cat_brandmeldetechnik', slug: 'brandmeldetechnik', name: 'Brandmeldetechnik', sort: 30, icon: 'flame' },
  'zutrittskontrolle': { id: 'cat_zutrittskontrolle', slug: 'zutrittskontrolle', name: 'Zutrittskontrolle', sort: 40, icon: 'key-round' },
  'stromversorgung-infrastruktur': { id: 'cat_stromversorgung', slug: 'stromversorgung-infrastruktur', name: 'Stromversorgung & Gehäusetechnik', sort: 50, icon: 'zap' },
  'weitere-produkte': { id: 'cat_weitere', slug: 'weitere-produkte', name: 'Weitere Produkte', sort: 60, icon: 'layers' },
};

const CATEGORY_BY_SLUG = new Map(SHOP_CATEGORIES.map((category) => [category.slug, category]));

const CATEGORY_SLUG_ALIASES = new Map([
  ['alarmanlagen', 'alarmtechnik'],
  ['einbruchschutz', 'alarmtechnik'],
  ['gefahrenmeldetechnik', 'alarmtechnik'],
  ['kameras', 'videoueberwachung'],
  ['video', 'videoueberwachung'],
  ['zutritt', 'zutrittskontrolle'],
  ['zutritt-zeit', 'zutrittskontrolle'],
  ['infrastruktur', 'stromversorgung-infrastruktur'],
  ['stromversorgung', 'stromversorgung-infrastruktur'],
  ['stromversorgung-und-infrastruktur', 'stromversorgung-infrastruktur'],
  ['netzteile', 'stromversorgung-infrastruktur'],
  ['weitere', 'weitere-produkte'],
  ['sonstiges', 'weitere-produkte'],
  ['brandmeldung', 'brandmeldetechnik'],
  ['brandmeldung-rettung', 'brandmeldetechnik'],
]);

const ACCESS_SUBCATEGORIES = new Set([
  'controller',
  'terminals-leser',
  'transponder',
  'klinkenwaechter',
  'systemeinrichtung',
]);

const ROOT_TO_CATEGORY = new Map(
  SHOP_CATEGORIES.flatMap((category) =>
    category.roots.map((root) => [normalizeText(root), category.slug]),
  ),
);

const IGNORED_ROOTS = new Set([
  'neuigkeiten',
  'unkategorisiert',
  '6 vdc',
  '8 vdc',
  'geregelte iris',
  'manuelle iris',
]);

/**
 * Normalizes text for taxonomy matching and comparisons.
 * @param {string} value 
 * @returns {string}
 */
export function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\u00df/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Creates URL-friendly slug, converting German umlauts and handling suffix/SKU.
 * @param {string} value
 * @param {string|number} [suffix]
 * @returns {string}
 */
export function slugify(value, suffix = '') {
  let str = String(value ?? '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

  if (suffix) {
    const cleanSuffix = String(suffix).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (cleanSuffix) {
      if (!str) {
        str = cleanSuffix;
      } else if (!str.endsWith(cleanSuffix)) {
        str = `${str}-${cleanSuffix}`;
      }
    }
  }
  return str;
}

export function slugifyTaxonomyValue(value) {
  return normalizeText(value).replace(/\s+/g, '-');
}

/**
 * Resolves category aliases to canonical slug.
 * @param {string} slug 
 * @returns {string}
 */
export function resolveCategorySlug(slug) {
  const requestedSlug = String(slug || '').trim();
  if (CATEGORY_BY_SLUG.has(requestedSlug)) {
    return requestedSlug;
  }

  const normalizedSlug = slugifyTaxonomyValue(requestedSlug);
  if (CATEGORY_BY_SLUG.has(normalizedSlug)) {
    return normalizedSlug;
  }

  return CATEGORY_SLUG_ALIASES.get(normalizedSlug) || '';
}

/**
 * Returns canonical category by slug or alias.
 * @param {string} slug 
 * @returns {object|null}
 */
export function getCategoryBySlug(slug) {
  return CATEGORY_BY_SLUG.get(resolveCategorySlug(slug)) || null;
}

/**
 * Returns subcategory object by parent category slug and subcategory slug.
 * @param {string} categorySlug 
 * @param {string} subcategorySlug 
 * @returns {object|null}
 */
export function getSubcategoryBySlug(categorySlug, subcategorySlug) {
  const category = getCategoryBySlug(categorySlug);
  if (!category) {
    return null;
  }
  return category.subcategories.find((subcategory) => subcategory.slug === subcategorySlug) || null;
}

/**
 * Returns all active canonical categories.
 * @returns {Array<object>}
 */
export function getAllCategories() {
  return SHOP_CATEGORIES;
}

/**
 * Formats navigation breadcrumbs for category / subcategory views.
 * @param {string} categorySlug 
 * @param {string} [subcategorySlug] 
 * @returns {Array<{ slug: string, label: string, path: string }>}
 */
export function formatCategoryBreadcrumb(categorySlug, subcategorySlug = '') {
  const category = getCategoryBySlug(categorySlug);
  if (!category) return [];

  const breadcrumbs = [
    { slug: category.slug, label: category.label, path: `/catalog/${category.slug}` }
  ];

  if (subcategorySlug) {
    const subcategory = getSubcategoryBySlug(category.slug, subcategorySlug);
    if (subcategory) {
      breadcrumbs.push({
        slug: subcategory.slug,
        label: subcategory.label,
        path: `/catalog/${category.slug}/${subcategory.slug}`,
      });
    }
  }

  return breadcrumbs;
}

/**
 * Splits comma-separated category strings while preserving decimal values and descriptors.
 * @param {string} rawCategoryPath 
 * @returns {Array<string>}
 */
export function splitCategoryCandidates(rawCategoryPath) {
  const str = String(rawCategoryPath ?? '').trim();
  if (!str) return [];

  const parts = [];
  let current = '';
  const rawParts = str.split(',');

  for (let i = 0; i < rawParts.length; i++) {
    const rawSegment = rawParts[i];
    const part = rawSegment.trim();
    if (!part) continue;

    if (!current) {
      current = part;
    } else {
      const isNewPath = /^(Videoüberwachung|Gefahrenmeldetechnik|PULSAR|Brandmeldetechnik|Zutrittskontrolle)/i.test(part) || part.includes('>');
      const isDecimal = /^\d/.test(part) && /\d$/.test(current);

      if (isNewPath && !isDecimal) {
        const cleaned = current.replace(/^[\s,]+|[\s,]+$/g, '').trim();
        if (cleaned) parts.push(cleaned);
        current = part;
      } else {
        current += ',' + rawSegment;
      }
    }
  }
  if (current) {
    const cleaned = current.replace(/^[\s,]+|[\s,]+$/g, '').trim();
    if (cleaned) parts.push(cleaned);
  }
  return parts.filter(Boolean);
}

function splitCategorySegments(candidate) {
  return String(candidate ?? '')
    .split('>')
    .map((part) => part.trim())
    .filter(Boolean);
}

function matchesPathPrefix(path, prefix) {
  if (path === prefix || path.startsWith(`${prefix} >`)) {
    return true;
  }
  const cleanPath = path.replace(/\s*>\s*/g, ' ');
  return cleanPath === prefix || cleanPath.startsWith(`${prefix} `) || cleanPath.endsWith(` ${prefix}`) || cleanPath.includes(` ${prefix} `);
}

function resolveManufacturerSubcategory(category, manufacturer) {
  const normalizedManufacturer = normalizeText(manufacturer);
  if (!normalizedManufacturer) {
    return null;
  }

  return category.subcategories.find((subcategory) =>
    (subcategory.manufacturerHints || []).some((hint) => {
      const normalizedHint = normalizeText(hint);
      return normalizedManufacturer.includes(normalizedHint) || normalizedHint.includes(normalizedManufacturer);
    }),
  ) || null;
}

function resolveSubcategory(category, segments, product = {}) {
  const manufacturerSubcategory = resolveManufacturerSubcategory(category, product.manufacturer);
  if (manufacturerSubcategory) {
    return manufacturerSubcategory;
  }

  const normalizedPath = segments.slice(1).map(normalizeText).filter(Boolean).join(' > ');
  if (!normalizedPath) {
    return null;
  }

  return category.subcategories.find((subcategory) =>
    subcategory.matchPrefixes.some((prefix) => matchesPathPrefix(normalizedPath, normalizeText(prefix))),
  ) || null;
}

function pickBestMatch(matches) {
  return [...matches].sort((left, right) => {
    const leftIsExplicitAccess = left.root === 'zutrittskontrolle'
      && ACCESS_SUBCATEGORIES.has(left.subcategory?.slug);
    const rightIsExplicitAccess = right.root === 'zutrittskontrolle'
      && ACCESS_SUBCATEGORIES.has(right.subcategory?.slug);

    if (leftIsExplicitAccess !== rightIsExplicitAccess) {
      return leftIsExplicitAccess ? -1 : 1;
    }

    if (left.root === 'pulsar' && right.root !== 'pulsar') {
      return 1;
    }
    if (right.root === 'pulsar' && left.root !== 'pulsar') {
      return -1;
    }

    if (Boolean(left.subcategory) !== Boolean(right.subcategory)) {
      return left.subcategory ? -1 : 1;
    }

    return right.category.priority - left.category.priority;
  })[0] || null;
}

/**
 * Primary taxonomy classification function. Resolves an ASL Warengruppe path into
 * canonical category and subcategory descriptors.
 * 
 * @param {string} rawCategoryPath 
 * @param {object} [product={}] 
 * @returns {{
 *   category: { id: string, slug: string, label: string, shortLabel: string, description: string, icon: string },
 *   subcategory: { slug: string, label: string, description: string, icon: string } | null,
 *   sourcePath: string,
 *   breadcrumbs: Array<{ slug: string, label: string }>
 * }}
 */
export function resolveProductTaxonomy(rawCategoryPath, product = {}) {
  const matches = splitCategoryCandidates(rawCategoryPath)
    .map((candidate) => {
      const segments = splitCategorySegments(candidate);
      const root = normalizeText(segments[0]);
      if (!root || IGNORED_ROOTS.has(root)) {
        return null;
      }

      const categorySlug = ROOT_TO_CATEGORY.get(root);
      if (!categorySlug) {
        return null;
      }

      const category = getCategoryBySlug(categorySlug);
      if (!category) {
        return null;
      }

      return {
        category,
        subcategory: resolveSubcategory(category, segments, product),
        sourcePath: candidate,
        root,
      };
    })
    .filter(Boolean);

  const bestMatch = pickBestMatch(matches);
  if (!bestMatch) {
    const mfg = normalizeText(product.manufacturer);
    if (mfg.includes('pulsar')) {
      const cat = getCategoryBySlug('stromversorgung-infrastruktur');
      const sub = getSubcategoryBySlug('stromversorgung-infrastruktur', 'netzteile');
      return {
        category: { id: cat.id, slug: cat.slug, label: cat.label, shortLabel: cat.shortLabel, description: cat.description, icon: cat.icon },
        subcategory: sub ? { slug: sub.slug, label: sub.label, description: sub.description, icon: sub.icon } : null,
        sourcePath: rawCategoryPath || '',
        breadcrumbs: [{ slug: cat.slug, label: cat.label }],
      };
    }
    if (mfg.includes('optex') || mfg.includes('satel')) {
      const cat = getCategoryBySlug('alarmtechnik');
      const sub = getSubcategoryBySlug('alarmtechnik', 'bewegungsmelder');
      return {
        category: { id: cat.id, slug: cat.slug, label: cat.label, shortLabel: cat.shortLabel, description: cat.description, icon: cat.icon },
        subcategory: sub ? { slug: sub.slug, label: sub.label, description: sub.description, icon: sub.icon } : null,
        sourcePath: rawCategoryPath || '',
        breadcrumbs: [{ slug: cat.slug, label: cat.label }],
      };
    }
    if (
      mfg.includes('dahua') ||
      mfg.includes('hikvision') ||
      mfg.includes('grundig') ||
      mfg.includes('luna')
    ) {
      const cat = getCategoryBySlug('videoueberwachung');
      const sub = getSubcategoryBySlug('videoueberwachung', 'ip-kameras');
      return {
        category: { id: cat.id, slug: cat.slug, label: cat.label, shortLabel: cat.shortLabel, description: cat.description, icon: cat.icon },
        subcategory: sub ? { slug: sub.slug, label: sub.label, description: sub.description, icon: sub.icon } : null,
        sourcePath: rawCategoryPath || '',
        breadcrumbs: [{ slug: cat.slug, label: cat.label }],
      };
    }
    if (mfg.includes('roger')) {
      const cat = getCategoryBySlug('zutrittskontrolle');
      const sub = getSubcategoryBySlug('zutrittskontrolle', 'terminals-leser');
      return {
        category: { id: cat.id, slug: cat.slug, label: cat.label, shortLabel: cat.shortLabel, description: cat.description, icon: cat.icon },
        subcategory: sub ? { slug: sub.slug, label: sub.label, description: sub.description, icon: sub.icon } : null,
        sourcePath: rawCategoryPath || '',
        breadcrumbs: [{ slug: cat.slug, label: cat.label }],
      };
    }
    if (mfg.includes('honeywell')) {
      const cat = getCategoryBySlug('brandmeldetechnik');
      const sub = getSubcategoryBySlug('brandmeldetechnik', 'honeywell-technik');
      return {
        category: { id: cat.id, slug: cat.slug, label: cat.label, shortLabel: cat.shortLabel, description: cat.description, icon: cat.icon },
        subcategory: sub ? { slug: sub.slug, label: sub.label, description: sub.description, icon: sub.icon } : null,
        sourcePath: rawCategoryPath || '',
        breadcrumbs: [{ slug: cat.slug, label: cat.label }],
      };
    }

    const fallbackCat = getCategoryBySlug('weitere-produkte');
    return {
      category: fallbackCat
        ? { id: fallbackCat.id, slug: fallbackCat.slug, label: fallbackCat.label, shortLabel: fallbackCat.shortLabel, description: fallbackCat.description, icon: fallbackCat.icon }
        : { id: 'cat_weitere', slug: 'weitere-produkte', label: 'Weitere Produkte', shortLabel: 'Weitere', description: '', icon: 'layers' },
      subcategory: null,
      sourcePath: rawCategoryPath || '',
      breadcrumbs: [{ slug: 'weitere-produkte', label: 'Weitere Produkte' }],
    };
  }

  const category = {
    id: bestMatch.category.id || CATEGORY_MAP[bestMatch.category.slug]?.id || 'cat_weitere',
    slug: bestMatch.category.slug,
    label: bestMatch.category.label,
    shortLabel: bestMatch.category.shortLabel,
    description: bestMatch.category.description,
    icon: bestMatch.category.icon,
  };
  const subcategory = bestMatch.subcategory
    ? {
        slug: bestMatch.subcategory.slug,
        label: bestMatch.subcategory.label,
        description: bestMatch.subcategory.description,
        icon: bestMatch.subcategory.icon,
      }
    : null;

  return {
    category,
    subcategory,
    sourcePath: bestMatch.sourcePath,
    breadcrumbs: [
      { slug: category.slug, label: category.label },
      ...(subcategory ? [{ slug: subcategory.slug, label: subcategory.label }] : []),
    ],
  };
}

/**
 * Converts price string or number into integer cents.
 * Handles German format "417,00 €", "1.250,50 EUR", US format "417.00", and raw integers.
 * @param {string|number|null|undefined} price 
 * @returns {number}
 */
export function parsePriceToCents(price) {
  if (price === null || price === undefined) return 0;
  if (typeof price === 'number') {
    if (isNaN(price) || !isFinite(price) || price <= 0) return 0;
    return Math.round(price * 100);
  }

  const str = String(price).trim();
  if (!str || str === '0') return 0;

  // Clean currency symbols, ISO codes, text units, and non-numeric chars except . , + -
  const cleaned = str
    .replace(/(EUR|USD|GBP|CHF|Euro|Dollar|Euros|Dollars)/gi, '')
    .replace(/[^\d.,+-]/g, '')
    .trim();

  if (!cleaned) return 0;

  // Format 1: "1.234,50" -> 1234.50 -> 123450 or "1,234.50" -> 1234.50 -> 123450
  if (cleaned.includes(',') && cleaned.includes('.')) {
    if (cleaned.indexOf('.') < cleaned.indexOf(',')) {
      const normalized = cleaned.replace(/\./g, '').replace(',', '.');
      const val = Number(normalized);
      return isNaN(val) || !isFinite(val) || val <= 0 ? 0 : Math.round(val * 100);
    } else {
      const normalized = cleaned.replace(/,/g, '');
      const val = Number(normalized);
      return isNaN(val) || !isFinite(val) || val <= 0 ? 0 : Math.round(val * 100);
    }
  }

  // Format 2: "899,00" or "-10,50" -> 899.00 / -10.50
  if (cleaned.includes(',')) {
    const normalized = cleaned.replace(',', '.');
    const val = Number(normalized);
    return isNaN(val) || !isFinite(val) || val <= 0 ? 0 : Math.round(val * 100);
  }

  // Format 3: "417" or "2.3" or "-50"
  const val = Number(cleaned);
  if (isNaN(val) || !isFinite(val) || val <= 0) return 0;
  return Math.round(val * 100);
}

/**
 * Formats integer cents to German currency string (e.g. 41700 -> "417,00 €").
 * @param {number|null|undefined} cents 
 * @returns {string}
 */
export function formatCentsToEuro(cents) {
  const c = Math.max(0, Math.round(cents || 0));
  const euros = c / 100;
  return euros.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Generates URL-friendly slug from title and SKU.
 * @param {string} title 
 * @param {string} [sku] 
 * @returns {string}
 */
export function generateProductSlug(title, sku = '') {
  const base = String(title || '').trim() || 'produkt';
  return slugify(base, sku);
}
